import {
  Box,
  Icon,
  Separator,
  Skeleton,
  Switch,
  Text,
} from "@salestrak/ui";
import { PermissionsSection } from "#app/components/settings/permissions/permissions-section";
import { useWorkspaceAdvancedPermissions } from "#app/lib/hooks/use-workspace-advanced-permissions";

// ─── Advanced Permissions ─────────────────────────────────────────────
// Two workspace-level toggles shown beneath the role / invite
// matrices. Reads + writes go through useWorkspaceAdvancedPermissions
// so Apollo stays out of this component file.
export function AdvancedPermissionsSection({ workspaceId }: { workspaceId: string }) {
  const {
    loading,
    ready,
    defaultPrivateWorkflows,
    adminOverrideEnabled,
    setField,
  } = useWorkspaceAdvancedPermissions(workspaceId);

  // The row copy is static — only the two switch values load. The rows
  // render for real and each switch stands in with a delayed bar (null
  // value), so the section never swaps layout on resolve.
  const pending = loading && !ready;
  return (
    <PermissionsSection icon="lock" title="Advanced Permissions">
      <Box gap="none">
        <AdvancedPermissionsRow
          icon="folder-kanban"
          title="Make new Workflows private"
          description="New Workflows start private"
          value={pending ? null : defaultPrivateWorkflows}
          onChange={(next) => setField("defaultPrivateWorkflows", next)}
        />
        <Separator />
        <AdvancedPermissionsRow
          icon="folder-kanban"
          title="Allow admins to manage anything private"
          description="Admins can add and remove Members and take ownership of any private Workflow, Project or View"
          value={pending ? null : adminOverrideEnabled}
          onChange={(next) => setField("adminOverrideEnabled", next)}
        />
      </Box>
    </PermissionsSection>
  );
}

function AdvancedPermissionsRow({
  icon,
  title,
  description,
  value,
  onChange,
}: {
  icon: "folder-kanban";
  title: string;
  description: string;
  /** null = value still loading — the switch slot shows a delayed bar. */
  value: boolean | null;
  onChange: (next: boolean) => void;
}) {
  return (
    <Box direction="row" align="center" gap="md" paddingY="md">
      {value === null ? (
        <Skeleton delayed className="rounded-full" height={16} width={28} />
      ) : (
        <Switch aria-label={title} checked={value} onCheckedChange={onChange} />
      )}
      <Box flex={1} gap="2xs">
        <Box direction="row" align="center" gap="xs">
          <Icon name={icon} size="sm" color="muted" />
          <Text size="sm" weight="semibold">
            {title}
          </Text>
        </Box>
        <Text size="sm" variant="muted">
          {description}
        </Text>
      </Box>
    </Box>
  );
}
